import { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateNodeInternals } from 'reactflow';
import { HandleType } from '@/enums/handle-type.enum';
import { useFlowView } from '@/components/Recipe/FlowContext';
import { FF_COLLAPSE_HANDLES_BY_TYPE } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  COLLAPSE_REMEASURE_TAIL_MS,
  COLLAPSE_TRANSITION_MS,
  HANDLE_TRANSITION_DELAY_MS,
  buildRowLayoutKey,
  resolveHandleRows,
  type HandleRow,
  type InputHandleEntry,
} from './handleCollapse';

interface InvalidHandles {
  required?: string[];
  requiredEmpty?: string[];
}

interface UseHandleCollapseArgs {
  nodeId: string;
  isSelected?: boolean;
  /** Legacy `string[]` handles carry no type information, so only v2/v3 nodes can be grouped. */
  version: number | undefined;
  sortedInputHandles: InputHandleEntry[];
  resolveType: (key: string, fallback: HandleType | undefined) => HandleType;
  handleCollapse: { expandedTypes: HandleType[] } | undefined;
  invalidHandles: InvalidHandles | undefined;
}

interface UseHandleCollapseResult {
  /**
   * The rows to render, in order. When `handleCollapse` is `undefined` (mode off, or the flag is
   * off) every input is its own row, sequentially indexed and never collapsed — the same rows the
   * flat handle list would produce, just shaped as `HandleRow`s so the caller can render them
   * through the same component regardless of mode.
   */
  inputRows: HandleRow[];
  /**
   * Whether the feature flag is on, i.e. whether the caller should render through
   * {@link CollapsedInputHandles} at all. This is *not* "the node is currently collapsed" — a
   * flag-on node with the mode off still reports `true` here, because it renders through the same
   * component with every row expanded (see `inputRows`).
   */
  isCollapseFeatureEnabled: boolean;
  isGroupExpanded: (memberKeys: string[]) => boolean;
  groupHasError: (memberKeys: string[]) => boolean;
  toggleGroup: (type: HandleType, memberCount: number) => void;
}

/**
 * Everything collapse-by-type needs to render a node's input handles: the rows themselves, the
 * per-group predicates the group handle displays, and the toggle that writes the new state.
 *
 * Lives apart from `handleCollapse` because that module must stay free of React — the `unit` vitest
 * project runs `handleCollapse.ts`'s tests in a node environment with no DOM, so a value import from
 * a `.tsx` module would break it.
 *
 * Reads {@link FF_COLLAPSE_HANDLES_BY_TYPE} itself rather than taking it as a prop, matching
 * {@link useHandleCollapseAnalytics}: `DynamicNode2` can't tell "flag off" apart from "flag on, mode
 * off" through `handleCollapse` alone, since both pass `undefined`, but the caller here needs that
 * distinction to always render through the same (`CollapsedInputHandles`) subtree.
 */
export const useHandleCollapse = ({
  nodeId,
  isSelected = false,
  version,
  sortedInputHandles,
  resolveType,
  handleCollapse,
  invalidHandles,
}: UseHandleCollapseArgs): UseHandleCollapseResult => {
  const flowView = useFlowView();
  const isCollapseFeatureEnabled = useFeatureFlag(FF_COLLAPSE_HANDLES_BY_TYPE).value === true;

  const collapse = isCollapseFeatureEnabled && (version === 2 || version === 3) ? handleCollapse : undefined;
  const groupingBoundary = `${nodeId}:${collapse ? collapse.expandedTypes.join(',') : 'off'}`;
  const [snapshot, setSnapshot] = useState<{ boundary: string; types: Record<string, HandleType> } | undefined>();
  const holdMembership = Boolean(collapse && isSelected);
  const types = collapse
    ? Object.fromEntries(
        sortedInputHandles.map(([key, handle]) => [
          key,
          holdMembership && snapshot?.boundary === groupingBoundary && snapshot.types[key] !== undefined
            ? snapshot.types[key]
            : resolveType(key, handle.type),
        ]),
      )
    : {};
  // Adjust only this component's state during render so the first committed layout already uses
  // the reconciled membership. Real type resolution/validation never reads this display snapshot.
  if (holdMembership) {
    if (
      snapshot?.boundary !== groupingBoundary ||
      Object.keys(snapshot.types).join(',') !== Object.keys(types).join(',')
    ) {
      setSnapshot({ boundary: groupingBoundary, types });
    }
  } else if (snapshot !== undefined) {
    setSnapshot(undefined);
  }
  const inputRows = resolveHandleRows({
    sortedInputHandles,
    resolveType: (key) => types[key],
    collapse,
  });

  /**
   * Only writes the new collapse state now — reporting moved to `CollapsedInputHandles`, the
   * always-flag-gated component that mounts `useHandleCollapseAnalytics` (see that hook's caller in
   * `CollapsedInputHandles.tsx`). `memberCount` stays in the signature even though it is unused here,
   * so `CollapsedInputHandles`'s `onToggleGroup` wrapper — which calls `trackGroupToggled` after this
   * runs — can pass this straight through from `CollapsedGroupHandle`'s `onToggle` without reshaping it.
   */
  const toggleGroup = useCallback(
    (type: HandleType, _memberCount: number) => {
      const expandedTypes = handleCollapse?.expandedTypes ?? [];
      const wasExpanded = expandedTypes.includes(type);
      flowView.setHandleCollapse(nodeId, {
        expandedTypes: wasExpanded
          ? expandedTypes.filter((expandedType) => expandedType !== type)
          : [...expandedTypes, type],
      });
    },
    [flowView, nodeId, handleCollapse],
  );

  const isGroupExpanded = useCallback(
    (memberKeys: string[]) =>
      inputRows.some((row) => row.kind === 'handle' && memberKeys.includes(row.key) && !row.isCollapsed),
    [inputRows],
  );

  const groupHasError = useCallback(
    (memberKeys: string[]) =>
      memberKeys.some(
        (memberKey) =>
          invalidHandles?.required?.includes(memberKey) || invalidHandles?.requiredEmpty?.includes(memberKey),
      ),
    [invalidHandles],
  );

  useRowRemeasure({ nodeId, inputRows, isCollapseFeatureEnabled, version });

  return { inputRows, isCollapseFeatureEnabled, isGroupExpanded, groupHasError, toggleGroup };
};

// React Flow caches connection targets, so refresh them until moved handles finish animating.
const useRowRemeasure = ({
  nodeId,
  inputRows,
  isCollapseFeatureEnabled,
  version,
}: {
  nodeId: string;
  inputRows: HandleRow[];
  isCollapseFeatureEnabled: boolean;
  version: number | undefined;
}): void => {
  const updateNodeInternals = useUpdateNodeInternals();
  const rowLayoutKey = buildRowLayoutKey(inputRows);
  const lastRowIndex = Math.max(0, ...inputRows.map((row) => row.rowIndex));
  const previousRowLayoutKeyRef = useRef(rowLayoutKey);

  useEffect(() => {
    const previousRowLayoutKey = previousRowLayoutKeyRef.current;
    previousRowLayoutKeyRef.current = rowLayoutKey;

    const canGroupHandlesByRow = version === 2 || version === 3;
    if (!canGroupHandlesByRow) return;
    if (previousRowLayoutKey === rowLayoutKey) return;

    updateNodeInternals(nodeId);
    let frame = 0;
    const startedAt = performance.now();
    const staggerDelay = isCollapseFeatureEnabled ? 0 : lastRowIndex * HANDLE_TRANSITION_DELAY_MS;
    const settlesAt = COLLAPSE_TRANSITION_MS + COLLAPSE_REMEASURE_TAIL_MS + staggerDelay;
    const remeasure = () => {
      updateNodeInternals(nodeId);
      if (performance.now() - startedAt < settlesAt) frame = requestAnimationFrame(remeasure);
    };
    frame = requestAnimationFrame(remeasure);

    return () => cancelAnimationFrame(frame);
  }, [nodeId, updateNodeInternals, rowLayoutKey, lastRowIndex, isCollapseFeatureEnabled, version]);
};
